import { useState } from 'react';
import type { CategoryClustersDto, ClusterDetailDto, ClusterArticleDto } from '../types';
import './StatusPage.css';

interface ClusterAnalysisProps {
  categories: CategoryClustersDto[];
  selectedCategory: string | null;
  selectedSubCategory: string | null;
}

interface ClusterTier {
  name: string;
  range: string;
  articlesPerCluster: string;
  clusters: ClusterDetailDto[];
  totalSelectedArticles: number;
}

interface GroupedClusters {
  categoryName: string;
  subCategoryName: string | null;
  stats: CategoryClustersDto['stats'];
  clusters: ClusterDetailDto[];
  tiers: ClusterTier[];
  totalSelectedArticles: number;
}

export function ClusterAnalysis({ categories, selectedCategory, selectedSubCategory }: ClusterAnalysisProps) {
  // Filter categories based on selection
  const filteredCategories = categories.filter(c => {
    if (selectedCategory && c.categorySlug !== selectedCategory) return false;
    if (selectedSubCategory && c.subCategorySlug !== selectedSubCategory) return false;
    return true;
  });

  // Calculate tier distribution based on the diversity bucket algorithm
  const calculateTiers = (clusters: ClusterDetailDto[]): ClusterTier[] => {
    const sortedClusters = [...clusters].sort((a, b) => a.clusterRank - b.clusterRank);

    const megaClusters = sortedClusters.filter(c => c.clusterRank <= 3);
    const majorClusters = sortedClusters.filter(c => c.clusterRank >= 4 && c.clusterRank <= 8);
    const longTailClusters = sortedClusters.filter(c => c.clusterRank >= 9);

    return [
      {
        name: 'Mega Stories',
        range: 'Rank 1-3',
        articlesPerCluster: '3 articles',
        clusters: megaClusters,
        totalSelectedArticles: megaClusters.reduce((sum, c) => sum + (c.selectedArticles?.length || 0), 0),
      },
      {
        name: 'Major Stories',
        range: 'Rank 4-8',
        articlesPerCluster: '2 articles',
        clusters: majorClusters,
        totalSelectedArticles: majorClusters.reduce((sum, c) => sum + (c.selectedArticles?.length || 0), 0),
      },
      {
        name: 'Long Tail',
        range: 'Rank 9+',
        articlesPerCluster: '1 article',
        clusters: longTailClusters,
        totalSelectedArticles: longTailClusters.reduce((sum, c) => sum + (c.selectedArticles?.length || 0), 0),
      },
    ];
  };

  // Group cluster data by category/subcategory
  const groupedClusters: GroupedClusters[] = filteredCategories.map(category => {
    const clusters = category.clusters || [];
    const tiers = calculateTiers(clusters);
    const totalSelectedArticles = clusters.reduce((sum, c) => sum + (c.selectedArticles?.length || 0), 0);

    return {
      categoryName: category.categoryName,
      subCategoryName: category.subCategoryName,
      stats: category.stats,
      clusters,
      tiers,
      totalSelectedArticles,
    };
  });

  // Calculate aggregate metrics
  const totalClusters = filteredCategories.reduce((sum, c) => sum + c.stats.totalClusters, 0);
  const allClusters = filteredCategories.flatMap(c => c.clusters || []);
  const totalSelectedArticles = allClusters.reduce((sum, c) => sum + (c.selectedArticles?.length || 0), 0);
  const avgClusterSize = filteredCategories.length > 0
    ? filteredCategories.reduce((sum, c) => sum + c.stats.averageClusterSize, 0) / filteredCategories.length
    : 0;
  const avgBestScore = allClusters.length > 0
    ? allClusters.reduce((sum, c) => sum + c.bestScore, 0) / allClusters.length
    : 0;

  // State for collapsible sections
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(groupedClusters.map((_, index) => `group-${index}`))
  );
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const toggleCluster = (clusterId: string) => {
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        newSet.add(clusterId);
      }
      return newSet;
    });
  };

  const renderClusterWithArticles = (cluster: ClusterDetailDto, tierIndex: number) => {
    const isExpanded = expandedClusters.has(cluster.id);
    const hasArticles = cluster.selectedArticles && cluster.selectedArticles.length > 0;

    return (
      <div key={cluster.id} className={`cluster-analysis__cluster cluster-analysis__cluster--tier-${tierIndex}`}>
        <div
          className="cluster-analysis__cluster-header"
          onClick={() => hasArticles && toggleCluster(cluster.id)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && hasArticles) {
              e.preventDefault();
              toggleCluster(cluster.id);
            }
          }}
          role={hasArticles ? 'button' : undefined}
          tabIndex={hasArticles ? 0 : undefined}
        >
          <div className="cluster-analysis__cluster-info">
            <span className="cluster-analysis__cluster-rank">#{cluster.clusterRank}</span>
            <span className="cluster-analysis__cluster-title">{cluster.representativeTitle || 'Untitled Cluster'}</span>
          </div>
          <div className="cluster-analysis__cluster-meta">
            <span className="cluster-analysis__cluster-score">{cluster.bestScore.toFixed(3)}</span>
            <span className="cluster-analysis__cluster-items">{cluster.itemCount} items</span>
            {hasArticles && (
              <span className="cluster-analysis__cluster-selected">
                {cluster.selectedArticles.length} selected
              </span>
            )}
            {hasArticles && (
              <button
                className="cluster-analysis__cluster-toggle"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? '−' : '+'}
              </button>
            )}
          </div>
        </div>

        {isExpanded && hasArticles && (
          <div className="cluster-analysis__articles">
            {cluster.selectedArticles.map((article: ClusterArticleDto, idx: number) => (
              <div key={article.id} className="cluster-analysis__article">
                <div className="cluster-analysis__article-rank">
                  {article.selectionRank || idx + 1}
                </div>
                <div className="cluster-analysis__article-content">
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cluster-analysis__article-title"
                  >
                    {article.title}
                  </a>
                  <div className="cluster-analysis__article-meta">
                    <span className="cluster-analysis__article-source">{article.sourceName}</span>
                    <span className="cluster-analysis__article-score">
                      Score: {article.interestScore.toFixed(3)}
                    </span>
                    {article.wordCount > 0 && (
                      <span className="cluster-analysis__article-words">
                        {article.wordCount.toLocaleString()} words
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTierWithClusters = (tier: ClusterTier, tierIndex: number) => (
    <div key={tier.name} className="cluster-analysis__tier">
      <div className="cluster-analysis__tier-header">
        <div className="cluster-analysis__tier-info">
          <span className="cluster-analysis__tier-name">{tier.name}</span>
          <span className="cluster-analysis__tier-range">{tier.range}</span>
        </div>
        <div className="cluster-analysis__tier-stats">
          <span className="cluster-analysis__tier-count">
            {tier.clusters.length} clusters
          </span>
          <span className="cluster-analysis__tier-selected">
            {tier.totalSelectedArticles} articles selected
          </span>
        </div>
      </div>

      {tier.clusters.length > 0 && (
        <div className="cluster-analysis__clusters-list">
          {tier.clusters.map(cluster => renderClusterWithArticles(cluster, tierIndex))}
        </div>
      )}

      {tier.clusters.length === 0 && (
        <div className="cluster-analysis__tier-empty">
          No clusters in this tier
        </div>
      )}
    </div>
  );

  return (
    <div className="cluster-analysis">
      <div className="cluster-analysis__intro">
        <div className="cluster-analysis__header">
          <h3 className="cluster-analysis__title">Cluster & Selection Analysis</h3>
          <p className="cluster-analysis__subtitle">
            Diversity bucket algorithm ensures balanced coverage across story importance
          </p>
        </div>

        <div className="cluster-analysis__stats">
          <div className="cluster-analysis__stat-card">
            <div className="cluster-analysis__stat-value">{totalClusters}</div>
            <div className="cluster-analysis__stat-label">Total Clusters</div>
          </div>
          <div className="cluster-analysis__stat-card">
            <div className="cluster-analysis__stat-value">{avgClusterSize.toFixed(1)}</div>
            <div className="cluster-analysis__stat-label">Avg Cluster Size</div>
          </div>
          <div className="cluster-analysis__stat-card">
            <div className="cluster-analysis__stat-value">{avgBestScore.toFixed(3)}</div>
            <div className="cluster-analysis__stat-label">Avg Best Score</div>
          </div>
          <div className="cluster-analysis__stat-card cluster-analysis__stat-card--accent">
            <div className="cluster-analysis__stat-value">{totalSelectedArticles}</div>
            <div className="cluster-analysis__stat-label">Articles Selected</div>
          </div>
        </div>

        <div className="cluster-analysis__note">
          <strong>Selection Rules:</strong> Rank 1-3 get 3 articles, Rank 4-8 get 2 articles, Rank 9+ get 1 article.
          Domain deduplication prevents selecting 2 articles from the same domain within a cluster.
        </div>
      </div>

      {groupedClusters.length === 0 && (
        <div className="cluster-analysis__empty">
          <p>No cluster data available for the selected filters</p>
        </div>
      )}

      {groupedClusters.map((group, index) => {
        const groupId = `group-${index}`;
        const isExpanded = expandedGroups.has(groupId);
        const groupTitle = group.subCategoryName
          ? `${group.categoryName} > ${group.subCategoryName}`
          : group.categoryName;

        return (
          <div key={groupId} className="cluster-analysis__group">
            <div
              className="cluster-analysis__group-header"
              onClick={() => toggleGroup(groupId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(groupId);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <h4 className="cluster-analysis__group-title">{groupTitle}</h4>
              <div className="cluster-analysis__group-info">
                <span className="cluster-analysis__group-stat">
                  {group.stats.totalClusters} clusters
                </span>
                <span className="cluster-analysis__group-separator">•</span>
                <span className="cluster-analysis__group-stat cluster-analysis__group-stat--accent">
                  {group.totalSelectedArticles} selected
                </span>
                <button
                  className="cluster-analysis__group-toggle"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? '−' : '+'}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="cluster-analysis__group-content">
                <div className="cluster-analysis__group-stats">
                  <div className="cluster-analysis__group-stat-card">
                    <div className="cluster-analysis__group-stat-value">{group.stats.totalClusters}</div>
                    <div className="cluster-analysis__group-stat-label">Clusters</div>
                  </div>
                  <div className="cluster-analysis__group-stat-card">
                    <div className="cluster-analysis__group-stat-value">{group.stats.averageClusterSize.toFixed(1)}</div>
                    <div className="cluster-analysis__group-stat-label">Avg Size</div>
                  </div>
                  <div className="cluster-analysis__group-stat-card">
                    <div className="cluster-analysis__group-stat-value">{group.stats.multiItemCount}</div>
                    <div className="cluster-analysis__group-stat-label">Multi-Item</div>
                  </div>
                  <div className="cluster-analysis__group-stat-card cluster-analysis__group-stat-card--accent">
                    <div className="cluster-analysis__group-stat-value">{group.totalSelectedArticles}</div>
                    <div className="cluster-analysis__group-stat-label">Selected</div>
                  </div>
                </div>

                <div className="cluster-analysis__tiers">
                  {group.tiers.map((tier, tierIndex) => renderTierWithClusters(tier, tierIndex))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
